// src/trig.ts
var degToRad = (deg) => deg * Math.PI / 180;
var radToDeg = (rad) => rad * 180 / Math.PI;
var sinDeg = (deg) => Math.sin(degToRad(deg));
var cosDeg = (deg) => Math.cos(degToRad(deg));
var tanDeg = (deg) => Math.tan(degToRad(deg));
var OFFSET_MULTIPLIER = {
  10: 1 / sinDeg(10),
  22.5: 1 / sinDeg(22.5),
  30: 1 / sinDeg(30),
  45: 1 / sinDeg(45),
  60: 1 / sinDeg(60)
};
var SHRINK_PER_INCH = {
  10: tanDeg(5),
  22.5: tanDeg(11.25),
  30: tanDeg(15),
  45: tanDeg(22.5),
  60: tanDeg(30)
};
function assertUsableBendAngle(angle) {
  if (!Number.isFinite(angle) || angle <= 0 || angle >= 90) {
    throw new Error(`angle ${angle} must be greater than 0 and less than 90`);
  }
}
function offsetMultiplierForAngle(angle) {
  assertUsableBendAngle(angle);
  return OFFSET_MULTIPLIER[angle] ?? 1 / sinDeg(angle);
}
function shrinkPerInchForAngle(angle) {
  assertUsableBendAngle(angle);
  return SHRINK_PER_INCH[angle] ?? tanDeg(angle / 2);
}

// src/ninety.ts
function calculateNinety(input) {
  const { stubHeight, shoe } = input;
  if (!Number.isFinite(stubHeight) || stubHeight <= 0) {
    throw new Error("stubHeight must be positive");
  }
  if (stubHeight < shoe.takeUp) {
    throw new Error(
      `stubHeight (${stubHeight}") is less than shoe take-up (${shoe.takeUp}") - impossible bend`
    );
  }
  const markFromEnd = stubHeight - shoe.takeUp;
  const totalLength = stubHeight - shoe.gain;
  return { type: "ninety", markFromEnd, totalLength };
}

// src/back-to-back.ts
function calculateBackToBack(input) {
  const { stub1, stub2, betweenStubs, shoe } = input;
  if (![stub1, stub2, betweenStubs].every(Number.isFinite) || stub1 <= 0 || stub2 <= 0 || betweenStubs <= 0) {
    throw new Error("stub1, stub2, and betweenStubs must all be positive");
  }
  if (stub1 < shoe.takeUp) {
    throw new Error(`stub1 (${stub1}") is less than shoe take-up (${shoe.takeUp}")`);
  }
  const firstMark = stub1 - shoe.takeUp;
  const secondMark = firstMark + betweenStubs;
  const totalLength = stub1 + betweenStubs + stub2 - 2 * shoe.gain;
  return { type: "back-to-back", firstMark, secondMark, totalLength };
}

// src/offset.ts
function calculateOffset(input) {
  const { rise, angle, shoe } = input;
  if (!Number.isFinite(rise) || rise <= 0) {
    throw new Error("rise must be positive");
  }
  const multiplier = offsetMultiplierForAngle(angle);
  const shrinkPerInch = shrinkPerInchForAngle(angle);
  const distanceBetweenMarks = rise * multiplier;
  const shrink = rise * shrinkPerInch;
  const totalLength = distanceBetweenMarks + 2 * shoe.gain;
  return { type: "offset", distanceBetweenMarks, shrink, totalLength };
}

// src/saddle3.ts
var DEFAULT_CENTER_ANGLE = 45;
var MIN_CENTER_ANGLE = 10;
var MAX_CENTER_ANGLE = 60;
var COMMON_SADDLE3_RULES = {
  // 3-point saddle shrink per inch of obstruction (trade tables): ~3/16" at 45,
  // 1/4" at 60, 1/8" at 30 — half the single-offset shrink for the center angle.
  30: { centerAngle: 30, sideBendAngle: 15, sideMultiplier: 3.9, shrinkPerInch: 0.125 },
  45: { centerAngle: 45, sideBendAngle: 22.5, sideMultiplier: 2.5, shrinkPerInch: 0.1875 },
  60: { centerAngle: 60, sideBendAngle: 30, sideMultiplier: 2, shrinkPerInch: 0.25 }
};
function calculateSaddle3(input) {
  const { obstructionHeight, centerMark, shoe } = input;
  if (!Number.isFinite(obstructionHeight) || obstructionHeight <= 0) {
    throw new Error("obstructionHeight must be positive");
  }
  if (!Number.isFinite(centerMark)) {
    throw new Error("centerMark must be a finite number");
  }
  const rule = saddle3AngleRule(input.centerAngle ?? DEFAULT_CENTER_ANGLE);
  const sideOffset = obstructionHeight * rule.sideMultiplier;
  if (centerMark < sideOffset) {
    const minCenterMark = Math.ceil(sideOffset);
    throw new Error(
      `Saddle won't fit here
At this center bend the side marks spread too far, so the first one lands off the end of the conduit. Move the center mark to at least ${minCenterMark}", or use a steeper center bend.`
    );
  }
  const firstSideMark = centerMark - sideOffset;
  const secondSideMark = centerMark + sideOffset;
  const shrink = obstructionHeight * rule.shrinkPerInch;
  const totalLength = secondSideMark - firstSideMark + 3 * shoe.gain;
  return {
    type: "saddle3",
    firstSideMark,
    centerMark,
    secondSideMark,
    centerAngle: rule.centerAngle,
    sideBendAngle: rule.sideBendAngle,
    shrink,
    totalLength
  };
}
function saddle3AngleRule(centerAngle) {
  const normalized = roundToTenth(centerAngle);
  if (normalized < MIN_CENTER_ANGLE || normalized > MAX_CENTER_ANGLE) {
    throw new Error(
      `centerAngle must be between ${MIN_CENTER_ANGLE} and ${MAX_CENTER_ANGLE} degrees`
    );
  }
  const commonRule = COMMON_SADDLE3_RULES[normalized];
  if (commonRule) return commonRule;
  const sideBendAngle = normalized / 2;
  const sideAngleRadians = sideBendAngle * Math.PI / 180;
  return {
    centerAngle: normalized,
    sideBendAngle,
    sideMultiplier: 1 / Math.sin(sideAngleRadians),
    shrinkPerInch: Math.tan(sideAngleRadians / 2)
  };
}
function roundToTenth(value) {
  return Math.round(value * 10) / 10;
}

// src/saddle4.ts
function calculateSaddle4(input) {
  const { obstructionHeight, obstructionWidth, angle, startMark, shoe } = input;
  if (!Number.isFinite(obstructionHeight) || !Number.isFinite(obstructionWidth) || obstructionHeight <= 0 || obstructionWidth <= 0) {
    throw new Error("obstructionHeight and obstructionWidth must be positive");
  }
  if (!Number.isFinite(startMark)) {
    throw new Error("startMark must be a finite number");
  }
  const multiplier = offsetMultiplierForAngle(angle);
  const shrinkPerInch = shrinkPerInchForAngle(angle);
  const offsetSpacing = obstructionHeight * multiplier;
  const m0 = startMark;
  const m1 = m0 + offsetSpacing;
  const m2 = m1 + obstructionWidth;
  const m3 = m2 + offsetSpacing;
  const shrink = 2 * obstructionHeight * shrinkPerInch;
  const totalLength = m3 - m0 + 4 * shoe.gain;
  return { type: "saddle4", marks: [m0, m1, m2, m3], shrink, totalLength };
}

// src/kick.ts
function calculateKick(input) {
  const { stubHeight, kickOffset, kickAngle, shoe } = input;
  if (!Number.isFinite(stubHeight) || !Number.isFinite(kickOffset) || stubHeight <= 0 || kickOffset <= 0) {
    throw new Error("stubHeight and kickOffset must be positive");
  }
  if (stubHeight < shoe.takeUp) {
    throw new Error(`stubHeight (${stubHeight}") is less than shoe take-up (${shoe.takeUp}")`);
  }
  let multiplier;
  let shrinkPerInch;
  try {
    multiplier = offsetMultiplierForAngle(kickAngle);
    shrinkPerInch = shrinkPerInchForAngle(kickAngle);
  } catch {
    throw new Error(`kickAngle ${kickAngle} must be greater than 0 and less than 90`);
  }
  const ninetyMark = stubHeight - shoe.takeUp;
  const kickMarkDistance = kickOffset * multiplier;
  const shrink = kickOffset * shrinkPerInch;
  const totalLength = stubHeight - shoe.gain + kickMarkDistance + 2 * shoe.gain;
  return { type: "kick", ninetyMark, kickMarkDistance, shrink, totalLength };
}

// src/rolling-offset.ts
function calculateRollingOffset(input) {
  const { rise, roll, angle, shoe } = input;
  if (!Number.isFinite(rise) || !Number.isFinite(roll) || rise < 0 || roll < 0) {
    throw new Error("rise and roll must be non-negative");
  }
  if (rise === 0 && roll === 0) {
    throw new Error("rise and roll cannot both be zero");
  }
  const multiplier = offsetMultiplierForAngle(angle);
  const shrinkPerInch = shrinkPerInchForAngle(angle);
  const trueOffset = Math.sqrt(rise * rise + roll * roll);
  const rollAngle = radToDeg(Math.atan2(roll, rise));
  const distanceBetweenMarks = trueOffset * multiplier;
  const shrink = trueOffset * shrinkPerInch;
  const totalLength = distanceBetweenMarks + 2 * shoe.gain;
  return {
    type: "rolling-offset",
    trueOffset,
    rollAngle,
    distanceBetweenMarks,
    shrink,
    totalLength
  };
}

// src/parallel-offset.ts
function calculateParallelOffset(input) {
  const { rise, angle, spacing, fromEnd, conduitCount, shoe } = input;
  if (!Number.isFinite(rise) || rise <= 0) throw new Error("rise must be positive");
  if (!Number.isFinite(spacing) || spacing <= 0) throw new Error("spacing must be positive");
  if (!Number.isInteger(conduitCount) || conduitCount < 2) {
    throw new Error("conduitCount must be an integer of at least 2");
  }
  if (!Number.isFinite(fromEnd) || fromEnd < 0) throw new Error("fromEnd must be non-negative");
  const distanceBetweenMarks = rise * offsetMultiplierForAngle(angle);
  const shrink = rise * shrinkPerInchForAngle(angle);
  const progression = spacing * tanDeg(angle / 2);
  const runs = Array.from({ length: conduitCount }, (_, index) => {
    const markShift = index * progression;
    const firstMark = fromEnd + markShift;
    return {
      run: index + 1,
      markShift,
      firstMark,
      secondMark: firstMark + distanceBetweenMarks
    };
  });
  return {
    type: "parallel-offset",
    progression,
    distanceBetweenMarks,
    shrink,
    totalLength: distanceBetweenMarks + 2 * shoe.gain,
    runs
  };
}

// src/concentric.ts
function calculateConcentric(input) {
  const { innerRadius, spacing, angle, parallelCount } = input;
  if (!Number.isFinite(innerRadius) || innerRadius <= 0)
    throw new Error("innerRadius must be positive");
  if (!Number.isFinite(spacing) || spacing <= 0) throw new Error("spacing must be positive");
  if (!Number.isFinite(angle) || angle <= 0) throw new Error("angle must be positive");
  if (!Number.isInteger(parallelCount) || parallelCount < 1)
    throw new Error("parallelCount must be a positive integer");
  const angleRad = degToRad(angle);
  const parallels = [];
  for (let i = 0; i < parallelCount; i++) {
    const radius = innerRadius + i * spacing;
    const additionalLength = i * spacing * angleRad;
    parallels.push({ radius, additionalLength });
  }
  return { type: "concentric", parallels };
}

// src/segmented.ts
function calculateSegmented(input) {
  const { totalRadius, totalAngle, segmentAngle } = input;
  if (!Number.isFinite(totalRadius) || totalRadius <= 0)
    throw new Error("totalRadius must be positive");
  if (!Number.isFinite(totalAngle) || totalAngle <= 0)
    throw new Error("totalAngle must be positive");
  if (!Number.isFinite(segmentAngle) || segmentAngle <= 0)
    throw new Error("segmentAngle must be positive");
  const ratio = totalAngle / segmentAngle;
  if (Math.abs(ratio - Math.round(ratio)) > 1e-9) {
    throw new Error(
      `totalAngle (${totalAngle}) must be evenly divisible by segmentAngle (${segmentAngle})`
    );
  }
  const segmentCount = Math.round(ratio);
  const chordLength = 2 * totalRadius * Math.sin(degToRad(segmentAngle) / 2);
  const totalLength = chordLength * segmentCount;
  return {
    type: "segmented",
    segmentCount,
    chordLength,
    markSpacing: chordLength,
    totalLength
  };
}

export { OFFSET_MULTIPLIER, SHRINK_PER_INCH, calculateBackToBack, calculateConcentric, calculateKick, calculateNinety, calculateOffset, calculateParallelOffset, calculateRollingOffset, calculateSaddle3, calculateSaddle4, calculateSegmented, cosDeg, degToRad, radToDeg, sinDeg, tanDeg };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map